/**
 * INTÉRPRETE DE URL AMIGABLE PARA CATÁLOGO
 * 
 * Este módulo se encarga de interpretar URLs amigables del catálogo y convertir
 * los slugs (texto en minúsculas con guiones) a los valores originales para
 * realizar consultas a la base de datos.
 * 
 * Formato de URL esperado:
 * /catalogo/[categoria]/[subcategoria]?marca=[marca]&canal=[canal]&linea=[linea]&concepto=[concepto]&precio_min=[min]&precio_max=[max]&filtro=[filtro]
 * 
 * Ejemplo:
 * /catalogo/laptops/gaming?marca=asus&canal=americanas&linea=tuf-gaming&concepto=gaming-de-entrada&precio_min=75&precio_max=11599&filtro=asus
 */

/**
 * Convierte un slug (texto-con-guiones) a texto normal en mayúsculas (TEXTO CON GUIONES)
 * @param {string} slug - El slug a convertir
 * @returns {string} - El texto convertido con espacios y en mayúsculas
 */
export const slugToText = (slug) => {
    if (!slug || typeof slug !== 'string') return '';
    
    return slug
        .split('-')
        .join(' ')
        .toUpperCase();
};

/**
 * Extrae los parámetros de la URL del catálogo
 * @param {string} pathname - La ruta de la URL (ej: /catalogo/laptops/gaming)
 * @param {URLSearchParams} searchParams - Los parámetros de consulta de la URL
 * @returns {Object} - Objeto con los parámetros extraídos
 */
export const parseUrlAmigable = (pathname, searchParams) => {
    console.log('🔍 parseUrlAmigable - Iniciando interpretación de URL');
    console.log('🔍 parseUrlAmigable - pathname:', pathname);
    console.log('🔍 parseUrlAmigable - searchParams:', Object.fromEntries(searchParams));
    
    // Extraer categoría y subcategoría del pathname
    const pathParts = pathname.split('/').filter(part => part !== '');
    
    // Estructura esperada: ['catalogo', 'categoria', 'subcategoria']
    const categoria = pathParts[1] ? slugToText(pathParts[1]) : null;
    const subcategoria = pathParts[2] ? slugToText(pathParts[2]) : null;
    
    // Extraer parámetros de consulta
    const marca = searchParams.get('marca') ? slugToText(searchParams.get('marca')) : null;
    const canal = searchParams.get('canal') ? slugToText(searchParams.get('canal')) : null;
    const linea = searchParams.get('linea') ? slugToText(searchParams.get('linea')) : null;
    const concepto = searchParams.get('concepto') ? slugToText(searchParams.get('concepto')) : null;
    const precioMin = searchParams.get('precio_min') ? parseInt(searchParams.get('precio_min'), 10) : null;
    const precioMax = searchParams.get('precio_max') ? parseInt(searchParams.get('precio_max'), 10) : null;
    const filtro = searchParams.get('filtro') ? slugToText(searchParams.get('filtro')) : null;
    
    const parametros = {
        categoria,
        subcategoria,
        marca,
        canal,
        linea,
        concepto,
        precioMin,
        precioMax,
        filtro
    };
    
    console.log('✅ parseUrlAmigable - Parámetros extraídos:', parametros);
    
    return parametros;
};

/**
 * Obtiene el ID y objeto completo de un canal por su nombre
 * @param {string} nombre - Nombre del canal
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerCanalPorNombre = async (nombre, baseUrl) => {
    if (!nombre) return null;
    
    try {
        console.log(`🔍 obtenerCanalPorNombre - Buscando canal: ${nombre}`);
        const response = await fetch(`${baseUrl}/catalog/channel-by-name?nombre=${encodeURIComponent(nombre)}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerCanalPorNombre - Canal encontrado:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerCanalPorNombre - Canal no encontrado: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerCanalPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Obtiene el ID y objeto completo de una categoría por su nombre
 * @param {string} nombre - Nombre de la categoría
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerCategoriaPorNombre = async (nombre, baseUrl) => {
    if (!nombre) return null;
    
    try {
        console.log(`🔍 obtenerCategoriaPorNombre - Buscando categoría: ${nombre}`);
        const response = await fetch(`${baseUrl}/catalog/category-by-name?nombre=${encodeURIComponent(nombre)}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerCategoriaPorNombre - Categoría encontrada:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerCategoriaPorNombre - Categoría no encontrada: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerCategoriaPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Obtiene el ID y objeto completo de una subcategoría por su nombre y categoría
 * @param {string} nombre - Nombre de la subcategoría
 * @param {number} idCategoria - ID de la categoría padre
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerSubcategoriaPorNombre = async (nombre, idCategoria, baseUrl) => {
    if (!nombre || !idCategoria) return null;
    
    try {
        console.log(`🔍 obtenerSubcategoriaPorNombre - Buscando subcategoría: ${nombre} en categoría ${idCategoria}`);
        const response = await fetch(`${baseUrl}/catalog/subcategory-by-name?nombre=${encodeURIComponent(nombre)}&id_categoria=${idCategoria}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerSubcategoriaPorNombre - Subcategoría encontrada:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerSubcategoriaPorNombre - Subcategoría no encontrada: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerSubcategoriaPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Obtiene el ID y objeto completo de una marca por su nombre y categoría
 * @param {string} nombre - Nombre de la marca
 * @param {number} idCategoria - ID de la categoría
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerMarcaPorNombre = async (nombre, idCategoria, baseUrl) => {
    if (!nombre || !idCategoria) return null;
    
    try {
        console.log(`🔍 obtenerMarcaPorNombre - Buscando marca: ${nombre} en categoría ${idCategoria}`);
        const response = await fetch(`${baseUrl}/catalog/brand-by-name?nombre=${encodeURIComponent(nombre)}&id_categoria=${idCategoria}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerMarcaPorNombre - Marca encontrada:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerMarcaPorNombre - Marca no encontrada: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerMarcaPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Obtiene el ID y objeto completo de una línea de marca por su nombre y marca
 * @param {string} nombre - Nombre de la línea
 * @param {number} idMarca - ID de la marca
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerLineaPorNombre = async (nombre, idMarca, baseUrl) => {
    if (!nombre || !idMarca) return null;
    
    try {
        console.log(`🔍 obtenerLineaPorNombre - Buscando línea: ${nombre} en marca ${idMarca}`);
        const response = await fetch(`${baseUrl}/catalog/brand-line-by-name?nombre=${encodeURIComponent(nombre)}&id_marca=${idMarca}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerLineaPorNombre - Línea encontrada:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerLineaPorNombre - Línea no encontrada: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerLineaPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Obtiene el ID y objeto completo de un concepto por su nombre y categoría
 * @param {string} nombre - Nombre del concepto
 * @param {number} idCategoria - ID de la categoría
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<{id: number, objeto: Object} | null>}
 */
export const obtenerConceptoPorNombre = async (nombre, idCategoria, baseUrl) => {
    if (!nombre || !idCategoria) return null;
    
    try {
        console.log(`🔍 obtenerConceptoPorNombre - Buscando concepto: ${nombre} en categoría ${idCategoria}`);
        const response = await fetch(`${baseUrl}/catalog/concept-by-name?nombre=${encodeURIComponent(nombre)}&id_categoria=${idCategoria}`);
        const data = await response.json();
        
        if (data.exito && data.datos && Array.isArray(data.datos) && data.datos.length > 0) {
            console.log(`✅ obtenerConceptoPorNombre - Concepto encontrado:`, data.datos[0]);
            return {
                id: data.datos[0].id,
                objeto: data.datos[0]
            };
        }
        
        console.log(`⚠️ obtenerConceptoPorNombre - Concepto no encontrado: ${nombre}`);
        return null;
    } catch (error) {
        console.error(`❌ obtenerConceptoPorNombre - Error:`, error);
        return null;
    }
};

/**
 * Función principal que interpreta una URL amigable y obtiene todos los IDs necesarios
 * @param {string} pathname - La ruta de la URL
 * @param {URLSearchParams} searchParams - Los parámetros de consulta
 * @param {string} baseUrl - URL base de la API
 * @returns {Promise<Object>} - Objeto con todos los IDs y objetos encontrados
 */
export const interpretarUrlAmigable = async (pathname, searchParams, baseUrl) => {
    console.log('🚀 interpretarUrlAmigable - Iniciando interpretación completa');
    
    // Paso 1: Extraer parámetros de la URL
    const parametros = parseUrlAmigable(pathname, searchParams);
    
    // Paso 2: Obtener IDs de forma secuencial (algunos dependen de otros)
    const resultado = {
        parametros,
        ids: {},
        objetos: {},
        filtrosParaProductos: {}
    };
    
    try {
        // Obtener canal (independiente)
        if (parametros.canal) {
            const canalData = await obtenerCanalPorNombre(parametros.canal, baseUrl);
            if (canalData) {
                resultado.ids.canal = canalData.id;
                resultado.objetos.canal = canalData.objeto;
                resultado.filtrosParaProductos.canal = canalData.id;
            }
        }
        
        // Obtener categoría (independiente)
        if (parametros.categoria) {
            const categoriaData = await obtenerCategoriaPorNombre(parametros.categoria, baseUrl);
            if (categoriaData) {
                resultado.ids.categoria = categoriaData.id;
                resultado.objetos.categoria = categoriaData.objeto;
                resultado.filtrosParaProductos.categoria = categoriaData.id;
            }
        }
        
        // Obtener subcategoría (depende de categoría)
        if (parametros.subcategoria && resultado.ids.categoria) {
            const subcategoriaData = await obtenerSubcategoriaPorNombre(parametros.subcategoria, resultado.ids.categoria, baseUrl);
            if (subcategoriaData) {
                resultado.ids.subcategoria = subcategoriaData.id;
                resultado.objetos.subcategoria = subcategoriaData.objeto;
                resultado.filtrosParaProductos.subcategoria = subcategoriaData.id;
            }
        }
        
        // Obtener marca (depende de categoría)
        if (parametros.marca && resultado.ids.categoria) {
            const marcaData = await obtenerMarcaPorNombre(parametros.marca, resultado.ids.categoria, baseUrl);
            if (marcaData) {
                resultado.ids.marca = marcaData.id;
                resultado.objetos.marca = marcaData.objeto;
                resultado.filtrosParaProductos.marca = marcaData.id;
            }
        }
        
        // Obtener línea (depende de marca)
        if (parametros.linea && resultado.ids.marca) {
            const lineaData = await obtenerLineaPorNombre(parametros.linea, resultado.ids.marca, baseUrl);
            if (lineaData) {
                resultado.ids.linea = lineaData.id;
                resultado.objetos.linea = lineaData.objeto;
                resultado.filtrosParaProductos.linea = lineaData.id;
            }
        }
        
        // Obtener concepto (depende de categoría)
        if (parametros.concepto && resultado.ids.categoria) {
            const conceptoData = await obtenerConceptoPorNombre(parametros.concepto, resultado.ids.categoria, baseUrl);
            if (conceptoData) {
                resultado.ids.concepto = conceptoData.id;
                resultado.objetos.concepto = conceptoData.objeto;
                resultado.filtrosParaProductos.concepto = conceptoData.id;
            }
        }
        
        // Procesar parámetro filtro y convertirlo a search
        if (parametros.filtro) {
            // El filtro ya viene convertido a mayúsculas con espacios por slugToText
            resultado.filtrosParaProductos.search = parametros.filtro;
            console.log('🔍 interpretarUrlAmigable - Filtro convertido a search:', parametros.filtro);
        }
        
        // Agregar precios si están presentes
        if (parametros.precioMin !== null) {
            resultado.filtrosParaProductos.precioMin = parametros.precioMin;
        }
        if (parametros.precioMax !== null) {
            resultado.filtrosParaProductos.precioMax = parametros.precioMax;
        }
        
        console.log('✅ interpretarUrlAmigable - Interpretación completada:', resultado);
        return resultado;
        
    } catch (error) {
        console.error('❌ interpretarUrlAmigable - Error durante la interpretación:', error);
        return resultado; // Devolver lo que se pudo obtener
    }
};